import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Pile {
  name: string;
  body: string;
}

const PILES: Pile[] = [
  {
    name: 'The tableau',
    body: 'Eight columns holding the whole pack — seven cards in the first four, six in the rest. Every one of them is face up from the start. There is nothing to turn over and nothing to find out.',
  },
  {
    name: 'The cells',
    body: 'Four spaces, each holding exactly one card. A cell is somewhere to put a card that is in the way, and it stays there until you have somewhere better for it. Filling all four is how deals are lost.',
  },
  {
    name: 'The foundations',
    body: 'Four spaces, one per suit, each starting with the Ace and building up to the King. Filling all four wins the game.',
  },
];

export function FreecellHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Get all fifty-two cards onto the four foundations, Ace through King, one suit each — with
          every card in plain sight from the first move.
        </p>
      </header>

      <section className="prose__section">
        <h2>The table</h2>
        <ul className="prose__list">
          {PILES.map((pile) => (
            <li key={pile.name}>
              <div>
                <h3>{pile.name}</h3>
                <p>{pile.body}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="prose__section">
        <h2>Moving cards</h2>
        <p>
          The tableau builds <strong>down</strong> in <strong>alternating colours</strong>: a red nine
          takes a black eight, which takes a red seven. The foundations do the opposite —{' '}
          <strong>up</strong>, and <strong>in suit</strong>, starting with the Ace.
        </p>
        <p>
          Any card may move into an <strong>empty column</strong>. There is no King rule here, which
          makes an empty column the most valuable thing on the table — and, as below, worth more than
          it first looks.
        </p>
        <p>
          Click a card to lift it and the board lights up wherever it can go; click it again to send it
          straight to its foundation. Clicking a lifted card with nowhere sensible to go simply puts it
          back.
        </p>
      </section>

      <section className="prose__section">
        <h2>Why a run sometimes will not move</h2>
        <p>
          Strictly, FreeCell moves <strong>one card at a time</strong>. Dragging a whole sequence is a
          shortcut for the shuffling you would otherwise do by hand: park the cards you are carrying in
          the cells, move the bottom one, and fetch them back. So the length of run you can shift is
          not a rule but a sum — <strong>(free cells + 1)</strong>, doubled for every{' '}
          <strong>empty column</strong> you could stage cards through.
        </p>
        <p>
          Four cells free and no empty column carries five cards. Free up a column and it carries ten.
          Moving <em>into</em> that empty column, though, it is back to five — the column you are
          filling cannot also be the one you are staging through. The table tells you the current
          number under the board, so the arithmetic is never hidden, only the reason for it.
        </p>
      </section>

      <section className="prose__section">
        <h2>Nothing is hidden, so nothing is luck</h2>
        <p>
          This is the whole appeal. Klondike buries three-quarters of the pack and hands out deals that
          cannot be won however well you play. FreeCell deals everything face up, and almost every
          shuffle is solvable — of the classic thirty-two thousand numbered deals, exactly one is not.
        </p>
        <p>
          Which means a dead FreeCell board is not bad luck. You could see it coming, and there is no
          stock left to blame. Take fewer cells if that sounds too comfortable.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
